/**
 * Checklist markdown dentro a `istruzioni` e `note` dei task (issue #47).
 *
 * Convenzione: una riga che inizia con `- [ ]` o `- [x]` (indentazione
 * iniziale consentita, `- [X]` maiuscolo accettato in lettura) è una voce
 * di checklist. Un `- [ ]` a metà riga NON lo è. In scrittura lo stato
 * spuntato è sempre normalizzato a `- [x]`.
 *
 * Funzioni pure su stringhe: il testo resta la fonte di verità e ogni
 * toggle riscrive solo il marcatore della propria riga, preservando tutto
 * il resto (whitespace compreso).
 */

const RIGA_CHECKBOX = /^([ \t]*)- \[( |x|X)\](.*)$/;

export type RigaAnalizzata =
  | {
      tipo: 'checkbox';
      /** Indice della riga nel testo (split su \n): è la chiave per il toggle. */
      indice: number;
      spuntata: boolean;
      /** Indentazione iniziale, da preservare anche visivamente. */
      indent: string;
      /** Testo della voce, senza il marcatore e senza lo spazio che lo segue. */
      testo: string;
    }
  | { tipo: 'testo'; indice: number; testo: string };

/** Scompone il testo in righe, distinguendo le voci di checklist dal resto. */
export function analizzaRighe(testo: string): RigaAnalizzata[] {
  return testo.split('\n').map((riga, indice) => {
    const m = riga.match(RIGA_CHECKBOX);
    if (!m) {
      return { tipo: 'testo' as const, indice, testo: riga };
    }
    const [, indent, stato, resto] = m;
    return {
      tipo: 'checkbox' as const,
      indice,
      spuntata: stato !== ' ',
      indent,
      // via lo spazio dopo "]" e l'eventuale \r di un testo CRLF
      testo: resto.replace(/^ /, '').replace(/\r$/, ''),
    };
  });
}

/** Conta le voci di checklist: { fatte, totali }. Testo assente → 0/0. */
export function contaChecklist(testo: string | null | undefined): {
  fatte: number;
  totali: number;
} {
  if (!testo) return { fatte: 0, totali: 0 };
  let fatte = 0;
  let totali = 0;
  for (const riga of testo.split('\n')) {
    const m = riga.match(RIGA_CHECKBOX);
    if (!m) continue;
    totali += 1;
    if (m[2] !== ' ') fatte += 1;
  }
  return { fatte, totali };
}

/**
 * Inverte lo stato della voce alla riga `indice` e restituisce il testo nuovo.
 * Se la riga non esiste o non è una checkbox, il testo torna invariato.
 * `- [X]` viene normalizzato: il prossimo stato spuntato si scrive `- [x]`.
 */
export function toggleRiga(testo: string, indice: number): string {
  const righe = testo.split('\n');
  const riga = righe[indice];
  if (riga === undefined) return testo;

  const m = riga.match(RIGA_CHECKBOX);
  if (!m) return testo;

  const [, indent, stato, resto] = m;
  const nuovoStato = stato === ' ' ? 'x' : ' ';
  righe[indice] = `${indent}- [${nuovoStato}]${resto}`;
  return righe.join('\n');
}
